// API service for workflow backend
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.workflow.alokpandey.org';

export interface WorkflowNode {
  id: string;
  type: string;
  data: any;
  position: { x: number; y: number };
}

export interface WorkflowEdge {
  id: string;
  source: string;
  target: string;
  type?: string;
}

export interface Workflow {
  id?: string;
  name: string;
  description?: string;
  template_id?: string;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  configuration?: any;
  status?: string;
  version?: number;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  last_modified_by?: string;
}

export interface WorkflowTemplate {
  id: string;
  name: string;
  description?: string;
  is_predefined: boolean;
  category?: string;
  workflow?: {
    nodes: WorkflowNode[];
    edges: WorkflowEdge[];
  };
}

class WorkflowAPI {
  private baseURL: string;

  constructor(baseURL: string = API_BASE_URL) {
    // Add /api to the base URL if not already present
    this.baseURL = baseURL.endsWith('/api') ? baseURL : `${baseURL}/api`;
  }

  // Helper to get auth headers
  private getAuthHeaders(): HeadersInit {
    const accessToken = localStorage.getItem('accessToken');
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    };

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    } else {
      console.warn('No access token found in localStorage. User may need to log in.');
    }

    return headers;
  }

  // Helper to handle API responses with better error handling
  private async handleResponse(response: Response): Promise<any> {
    const data = await response.json();

    // Handle 401 Unauthorized - token expired or invalid
    if (response.status === 401) {
      console.error('Authentication failed. Token may be expired.');
      // Clear invalid tokens
      localStorage.removeItem('accessToken');
      localStorage.removeItem('idToken');
      localStorage.removeItem('user');
      // Redirect to login
      window.location.href = '/login';
      throw new Error('Authentication required. Please log in again.');
    }

    // Handle other errors
    if (!data.success) {
      throw new Error(data.error || data.message || 'API request failed');
    }

    return data;
  }

  // Workflows
  async getWorkflows(filters?: { status?: string; template_id?: string }): Promise<Workflow[]> {
    const params = new URLSearchParams(filters as any);
    const response = await fetch(`${this.baseURL}/workflows?${params}`, {
      headers: this.getAuthHeaders()
    });
    const data = await this.handleResponse(response);
    return data.data;
  }

  async getWorkflow(id: string): Promise<Workflow> {
    const response = await fetch(`${this.baseURL}/workflows/${id}`, {
      headers: this.getAuthHeaders()
    });
    const data = await this.handleResponse(response);
    return data.data;
  }

  async createWorkflow(workflow: Workflow): Promise<Workflow> {
    const response = await fetch(`${this.baseURL}/workflows`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(workflow)
    });
    const data = await this.handleResponse(response);
    return data.data;
  }

  async updateWorkflow(id: string, workflow: Partial<Workflow>): Promise<Workflow> {
    const response = await fetch(`${this.baseURL}/workflows/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(workflow)
    });
    const data = await this.handleResponse(response);
    return data.data;
  }

  async deleteWorkflow(id: string): Promise<void> {
    const response = await fetch(`${this.baseURL}/workflows/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    await this.handleResponse(response);
  }

  async executeWorkflow(id: string, params: {
    brdPath?: string;
    workspaceRoot?: string;
    contextDirs?: string;
    outputDir?: string;
  }): Promise<{
    workflow_id: string;
    execution_id: string;
    temporal_workflow_id: string;
    temporal_run_id: string;
    temporal_ui_url: string;
    message: string;
  }> {
    const response = await fetch(`${this.baseURL}/workflows/${id}/execute`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(params)
    });
    const data = await this.handleResponse(response);
    return data.data;
  }

  // Templates
  async getTemplates(filters?: { is_predefined?: boolean; category?: string }): Promise<WorkflowTemplate[]> {
    const params = new URLSearchParams(filters as any);
    const response = await fetch(`${this.baseURL}/templates?${params}`, {
      headers: this.getAuthHeaders()
    });
    const data = await this.handleResponse(response);
    return data.data;
  }

  async getSDLCTemplate(): Promise<WorkflowTemplate> {
    const response = await fetch(`${this.baseURL}/templates/sdlc`, {
      headers: this.getAuthHeaders()
    });
    const data = await this.handleResponse(response);
    return data.data;
  }

  async getTemplate(id: string): Promise<WorkflowTemplate> {
    const response = await fetch(`${this.baseURL}/templates/${id}`, {
      headers: this.getAuthHeaders()
    });
    const data = await this.handleResponse(response);
    return data.data;
  }

  async getWorkflowTemplates(): Promise<any[]> {
    const response = await fetch(`${this.baseURL}/workflows/templates/list`, {
      headers: this.getAuthHeaders()
    });
    const data = await this.handleResponse(response);
    return data.data;
  }

  // Executions
  async getExecutions(filters?: { workflow_id?: string; status?: string }): Promise<any[]> {
    const params = new URLSearchParams(filters as any);
    const response = await fetch(`${this.baseURL}/executions?${params}`, {
      headers: this.getAuthHeaders()
    });
    const data = await this.handleResponse(response);
    return data.data;
  }

  async getExecution(id: string): Promise<any> {
    const response = await fetch(`${this.baseURL}/executions/${id}`, {
      headers: this.getAuthHeaders()
    });
    const data = await this.handleResponse(response);
    return data.data;
  }

  async getExecutionLogs(id: string): Promise<{ logs: string }> {
    const response = await fetch(`${this.baseURL}/executions/${id}/logs`, {
      headers: this.getAuthHeaders()
    });
    const data = await this.handleResponse(response);
    return data.data;
  }

  async getExecutionArtifacts(id: string): Promise<{
    epic?: any;
    stories?: any[];
    epicMd?: string;
    storiesMd?: string;
  }> {
    const response = await fetch(`${this.baseURL}/executions/${id}/artifacts`, {
      headers: this.getAuthHeaders()
    });
    const data = await this.handleResponse(response);
    return data.data;
  }

  async approveExecution(id: string, artifacts?: { epic?: any; stories?: any[] }): Promise<void> {
    const response = await fetch(`${this.baseURL}/executions/${id}/approve`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ artifacts })
    });
    await this.handleResponse(response);
  }

  async rejectExecution(id: string, reason?: string): Promise<void> {
    const response = await fetch(`${this.baseURL}/executions/${id}/reject`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ reason })
    });
    await this.handleResponse(response);
  }

  async terminateExecution(id: string): Promise<void> {
    const response = await fetch(`${this.baseURL}/executions/${id}/terminate`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    });
    await this.handleResponse(response);
  }

  async deleteExecution(id: string): Promise<void> {
    const response = await fetch(`${this.baseURL}/executions/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    await this.handleResponse(response);
  }

  // Health check
  async healthCheck(): Promise<{ status: string; database: string }> {
    const response = await fetch(`${this.baseURL.replace('/api', '')}/health`);
    return await response.json();
  }
}

export const workflowAPI = new WorkflowAPI();
export default workflowAPI;

